import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Checkbox,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "./ServiceSelector.css";
import ServiceInfoModal from "./ServiceInfoModal";
import { useServiceSelector } from "./hooks/useServiceSelector";
import { useEffect } from "react";
import { Element, Errors } from "../../../Interface/scheduler/form";

interface ServiceSelectorProps {
  element: Element;
  errors: Errors;
  setErrors: React.Dispatch<React.SetStateAction<Errors>>;
}

const ServiceSelector = ({
  element,
  errors,
  setErrors,
}: ServiceSelectorProps) => {
  const {
    services,
    selectedServices,
    activeTab,
    loading,
    modalOpen,
    selectedServiceInfo,
    displayServices,
    hasPopularServices,
    handleServiceToggle,
    handleTabChange,
    handleInfoClick,
    handleModalClose,
  } = useServiceSelector();

  // Clear error when user selects at least one service
  useEffect(() => {
    if (selectedServices.length > 0 && errors?.[element?.id] && setErrors) {
      setErrors((prevErrors: Errors) => ({
        ...prevErrors,
        [element.id]: null, // Clear the error for this element
      }));
    }
  }, [selectedServices, errors, element?.id, setErrors]);

  const renderContent = () => {
    if (loading) {
      return (
        <Box className="service-selector__loading">
          <CircularProgress className="service-selector__spinner" />
        </Box>
      );
    }

    if (services.length === 0) {
      return (
        <Typography variant="body1" className="service-selector__empty">
          No services available
        </Typography>
      );
    }

    // If no popular services exist, show all services
    const servicesToDisplay = hasPopularServices ? displayServices : services;
    return (
      <>
        <Grid container spacing={2} className="service-selector__grid">
          {servicesToDisplay.map((service) => {
            const isSelected = selectedServices.includes(service.id);
            if (!service?.externalId) {
              return null;
            }
            return (
              <Grid item xs={12} sm={6} lg={4} key={service.id}>
                <Card
                  className="service-card"
                  onClick={() => handleServiceToggle(service.id)}
                  sx={{
                    border: isSelected ? "2px solid" : "1px solid #e0e0e0",
                    borderColor: isSelected ? "primary.main" : "#e0e0e0",
                    backgroundColor: isSelected
                      ? "rgba(25, 118, 210, 0.08)"
                      : "background.paper",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: isSelected
                        ? "rgba(25, 118, 210, 0.12)"
                        : "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  <CardContent className="service-card__content">
                    <Box className="service-card__inner">
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleServiceToggle(service.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="service-card__checkbox"
                        sx={{
                          color: isSelected ? "primary.main" : "default",
                          "&.Mui-checked": {
                            color: "primary.main",
                          },
                        }}
                      />

                      <Typography
                        variant="body1"
                        className="service-card__name"
                        sx={{
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? "primary.main" : "text.primary",
                          flex: 1,
                          marginLeft: 1,
                        }}
                      >
                        {service.name ? service.name : service.cesName}
                      </Typography>
                      {service?.description && (
                        <IconButton
                          size="small"
                          className="service-card__info-button"
                          onClick={(e) => handleInfoClick(e, service)}
                          sx={{
                            color: isSelected
                              ? "primary.main"
                              : "action.active",
                          }}
                        >
                          <InfoOutlinedIcon className="service-card__info-icon" />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        <ServiceInfoModal
          open={modalOpen}
          onClose={handleModalClose}
          service={selectedServiceInfo}
        />
        {errors?.[element?.id] && selectedServices.length === 0 && (
          <div
            className="error-message error-div"
            data-testid={"error-message-" + element?.id}
          >
            {errors[element?.id]}
          </div>
        )}
      </>
    );
  };
  return (
    <Box
      className={`service-selector ${errors?.[element?.id] && selectedServices.length === 0 ? "error" : ""}`}
    >
      <Typography variant="h2" className="service-selector__title" mt={2}>
        {element.title || "Select Your Services"}
      </Typography>

      {/* Only show tabs if there are popular services */}
      {hasPopularServices && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          className="service-selector__tabs"
          sx={{
            "& .MuiTab-root": {
              borderRadius: "25px",
              color: "#666",
              fontWeight: 500,
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "#fff",
              },
              "&:hover": {
                backgroundColor: "primary.main",
                color: "#fff",
              },
            },
          }}
        >
          <Tab label="Popular" />
          <Tab label="Other Services" />
        </Tabs>
      )}

      {renderContent()}
    </Box>
  );
};

export default ServiceSelector;
